<template>
  <div class="document-magnifier-preview">
    <div v-if="isRendering" class="text-center py-3">
      <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
    </div>
    <div v-else-if="renderError" class="text-danger small py-2">{{ renderError }}</div>

    <div
        v-for="(page, index) in pages"
        :key="index"
        class="page-wrapper"
        :class="{ 'mt-2': index > 0 }"
        @mouseenter="activePage = index"
        @mousemove="moveLens($event, page)"
        @mouseleave="activePage = null">
      <img :src="page" class="page-image" alt="" draggable="false"/>
      <div v-if="activePage === index" class="lens" :style="lensStyle"></div>
    </div>
  </div>
</template>

<script setup>
import * as pdfjsLib from 'pdfjs-dist';
import { defineProps, onBeforeUnmount, ref, watch } from 'vue';

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString();

const props = defineProps({
  file: {
    type: File,
    required: true,
  },
  zoom: {
    type: Number,
    default: 2.5,
  },
  lensSize: {
    type: Number,
    default: 220,
  },
});

// Render scale for PDF pages, high enough that the magnified lens stays sharp
const RENDER_SCALE = 3;

const pages       = ref([]);
const isRendering = ref(false);
const renderError = ref('');
const activePage  = ref(null);
const lensStyle   = ref({});

let objectUrls = [];

const revokeObjectUrls = () => {
  objectUrls.forEach(url => URL.revokeObjectURL(url));
  objectUrls = [];
};

const renderPdf = async (file) => {
  const pdf    = await pdfjsLib.getDocument({ data: await file.arrayBuffer() }).promise;
  const result = [];

  for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber++) {
    const page     = await pdf.getPage(pageNumber);
    const viewport = page.getViewport({ scale: RENDER_SCALE });
    const canvas   = document.createElement('canvas');

    canvas.width  = viewport.width;
    canvas.height = viewport.height;

    await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;

    const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
    const url  = URL.createObjectURL(blob);
    objectUrls.push(url);
    result.push(url);
  }

  await pdf.destroy();

  return result;
};

const loadFile = async (file) => {
  revokeObjectUrls();
  pages.value       = [];
  renderError.value = '';

  if (!file) return;

  isRendering.value = true;

  try {
    if (file.type === 'application/pdf' || file.name?.toLowerCase().endsWith('.pdf')) {
      pages.value = await renderPdf(file);
    } else if (file.type?.startsWith('image/')) {
      const url = URL.createObjectURL(file);
      objectUrls.push(url);
      pages.value = [ url ];
    } else {
      renderError.value = 'Vorschau für diesen Dateityp nicht verfügbar';
    }
  } catch (error) {
    renderError.value = 'Dokument konnte nicht angezeigt werden';
  } finally {
    isRendering.value = false;
  }
};

const moveLens = (event, pageSrc) => {
  const rect = event.currentTarget.getBoundingClientRect();
  const x    = event.clientX - rect.left;
  const y    = event.clientY - rect.top;
  const half = props.lensSize / 2;

  lensStyle.value = {
    width: `${props.lensSize}px`,
    height: `${props.lensSize}px`,
    left: `${x - half}px`,
    top: `${y - half}px`,
    backgroundImage: `url(${pageSrc})`,
    backgroundSize: `${rect.width * props.zoom}px ${rect.height * props.zoom}px`,
    backgroundPosition: `${half - x * props.zoom}px ${half - y * props.zoom}px`,
  };
};

watch(() => props.file, loadFile, { immediate: true });

onBeforeUnmount(revokeObjectUrls);
</script>

<style scoped>
.page-wrapper {
  position: relative;
  cursor: none;
  border: 1px solid #ddd;
}

.page-image {
  display: block;
  width: 100%;
  height: auto;
  user-select: none;
}

.lens {
  position: absolute;
  pointer-events: none;
  border: 2px solid var(--primary);
  border-radius: 50%;
  background-repeat: no-repeat;
  background-color: #fff;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
  z-index: 10;
}
</style>
